/**
 * The WHERE field for a result-grid column: the SQL that reads that column in
 * the statement that produced it, written the way the statement names its
 * source — `o.customer_id` for a join over `orders o`, `c.name` for a select
 * item `c.name AS customer`, the bare `name` for a one-table query. Used by
 * the results grid's "Add to WHERE" (the SQL view writes it, the Visual view
 * resolves the condition chip's column from it).
 *
 * Matching, most precise first:
 *   1. By position — the select list expanded to its output columns (`*` and
 *      `t.*` through the resolver's `starColumnList` / `fromItemStarColumns`,
 *      which need the schema snapshot) has as many columns as the result and
 *      the one at the result column's index carries its name. This tells the
 *      two `id` columns of `SELECT * FROM a JOIN b` apart even with no
 *      provenance (SQLite reports none).
 *   2. By name — one explicit select item named like the column (its alias,
 *      else its column name).
 *   3. By provenance — the driver's source table (`sourceSchema` /
 *      `sourceTable` / `sourceColumn`) bound by exactly one FROM item that
 *      exposes the column under the result's name: that item's alias, else
 *      its table token as the statement spells it (`provenanceSource` — a
 *      derived table or CTE over the table renames the column the provenance
 *      names: `SELECT * FROM (SELECT id AS x FROM t) s` is filtered by `x`,
 *      never by `id`).
 *   4. Unqualified — only when the statement reads a single source, or the
 *      expanded `*` columns carry the name once.
 *   5. By exposed names — the one FROM source that can hold a column of that
 *      name when every other one cannot (a derived table by its select list, a
 *      CTE by its column list, a base table by its snapshot columns), qualified
 *      with its alias: `s.x` (`soleSourceExposing`).
 * Anything else is `unknown`: writing the bare name there could be ambiguous
 * or name another table's column. An aggregate or window result cannot be
 * filtered in WHERE (`unfilterable`); another computed item is filtered by its
 * expression, parenthesised.
 */

import type { ParseOptions, SqlEngine } from '../../../sql-grammar/model';
import { readQualifiedName } from '../../../sql-resolver/scope/identifiers';
import { bindingKeyOf, querySources, querySourceOf, type QuerySource } from '../../../sql-resolver/scope/querySources';
import { fromItemStarColumns, starColumnList, type StarListColumn } from '../../../sql-resolver/scope/starColumns';
import { catalogIdentSql } from '../../../sql-resolver/suggest/sqlIdentifierText';
import type { FromItem, QueryStructure, SchemaSnapshot, SelectItem } from '../../../sql-resolver/types';
import { containsAggregateCall } from '../../visual-query-editor/detection/aggregateAwareness';
import { provenanceSource, sameName, selectItemName, soleSourceExposing } from './resultColumnSource';

/** A result-grid column as "Add to WHERE" sees it. */
export type ResultColumnTarget = {
  /** The output column name the driver reported. */
  readonly name: string;
  /** Its position in the result set, and the result set's column count. */
  readonly index: number;
  readonly count: number;
  readonly dataType?: string;
  /** Real or declared (loose) foreign key — Table Explorer's lookup default. */
  readonly isForeignKey?: boolean;
  readonly sourceSchema?: string;
  readonly sourceTable?: string;
  readonly sourceColumn?: string;
};

export type ResultColumnField =
  /** `computed`: an expression select item, filtered by its parenthesised expression — no table column. */
  | { readonly kind: 'field'; readonly sql: string; readonly computed?: true }
  | { readonly kind: 'unfilterable' }
  | { readonly kind: 'unknown' };

type Output = { readonly name?: string; readonly field: ResultColumnField };

const UNKNOWN: ResultColumnField = { kind: 'unknown' };

export function resultColumnWhereField(
  structure: QueryStructure,
  options: ParseOptions,
  target: ResultColumnTarget,
  snapshot?: SchemaSnapshot | null,
): ResultColumnField {
  // One WHERE cannot filter a UNION / INTERSECT / EXCEPT result.
  if (structure.compoundBranches?.length) return UNKNOWN;
  const engine = options.engine;
  const sources = querySources(structure, engine);
  const single = sources.length === 1;
  const items = structure.selectItems ?? [];

  const outputs = expandOutputs(structure, items, options, sources, snapshot);
  if (outputs && outputs.length === target.count) {
    const at = outputs[target.index];
    if (at && (at.name === undefined || sameName(at.name, target.name))) return at.field;
  }

  const explicit = items
    .map((item) => explicitOutput(item, options))
    .filter((o): o is Output => o !== null && o.name !== undefined && sameName(o.name, target.name));
  if (explicit.length === 1) return explicit[0]!.field;
  if (explicit.length > 1) return UNKNOWN;
  // No select item names it: it comes from a `*` / `t.*` (or the statement
  // changed since it ran), under the name its FROM source exposes.
  const bound = provenanceSource(structure, options, target, snapshot);
  if (bound === 'unknown') return UNKNOWN;
  // The catalog spelling (`sourceColumn`) — Oracle delimits a lower-case name.
  if (bound) return { kind: 'field', sql: columnSql(single ? null : bound, target.sourceColumn ?? target.name, engine) };
  const starred = outputs?.filter((o) => o.name !== undefined && sameName(o.name, target.name)) ?? [];
  if (starred.length === 1) return starred[0]!.field;
  if (starred.length > 1) return UNKNOWN;
  if (single) return { kind: 'field', sql: catalogIdentSql(target.name, engine) };
  const holder = soleSourceExposing(structure, options, target.name, snapshot);
  return holder ? { kind: 'field', sql: columnSql(holder, target.name, engine) } : UNKNOWN;
}

/** The select list as output columns; null when a `*` / `t.*` cannot be expanded. */
function expandOutputs(
  structure: QueryStructure,
  items: readonly SelectItem[],
  options: ParseOptions,
  sources: readonly QuerySource[],
  snapshot: SchemaSnapshot | null | undefined,
): Output[] | null {
  const engine = options.engine;
  const qualify = sources.length > 1;
  const out: Output[] = [];
  for (const item of items) {
    const text = item.expressionText.trim();
    let stars: readonly StarListColumn[] | null | undefined;
    if (text === '*') {
      const list = starColumnList(structure, options, snapshot);
      stars = list.kind === 'columns' ? list.columns : null;
    } else if (text.endsWith('.*')) {
      const fromItem = boundFromItem(structure, text.slice(0, -2), options);
      stars = fromItem ? fromItemStarColumns(fromItem, engine, snapshot) : null;
    }
    if (stars === null) return null;
    if (stars) {
      for (const c of stars) {
        const source = c.sharedBy ? null : sources.find((s) => s.exposedName === c.source.name) ?? null;
        out.push({
          name: c.name,
          // A USING / NATURAL column is written once, bare (Oracle forbids a qualifier on it).
          field: { kind: 'field', sql: columnSql(qualify ? source : null, c.name, engine) },
        });
      }
      continue;
    }
    const explicit = explicitOutput(item, options);
    if (!explicit) return null;
    out.push(explicit);
  }
  return out;
}

/** An explicit (non-star) select item as an output column: its name (alias, else the column's own) and field. */
function explicitOutput(item: SelectItem, options: ParseOptions): Output | null {
  const text = item.expressionText.trim();
  if (text === '*' || text.endsWith('.*')) return null;
  const { name, isColumnRef } = selectItemName(item, options);
  if (item.window || containsAggregateCall(text)) return { name, field: { kind: 'unfilterable' } };
  return { name, field: isColumnRef ? { kind: 'field', sql: text } : { kind: 'field', sql: `(${text})`, computed: true } };
}

/** The FROM item a `t.*` qualifier names (its alias, else its table name). */
function boundFromItem(structure: QueryStructure, qualifier: string, options: ParseOptions): FromItem | undefined {
  const ref = readQualifiedName(qualifier, 0, qualifier.length, options);
  if (!ref || ref.range.endOffset !== qualifier.length) return undefined;
  const key = bindingKeyOf(options.engine, ref.qualified.foldedName);
  const items = [...(structure.fromItems ?? []), ...(structure.joins ?? []).map((j) => j.right)];
  return items.find((i) => querySourceOf(i, options.engine).bindingKey === key);
}

function columnSql(source: QuerySource | null, column: string, engine: SqlEngine): string {
  const name = catalogIdentSql(column, engine);
  return source?.qualifierSql ? `${source.qualifierSql}.${name}` : name;
}
